import React, { useState, useEffect } from 'react';
import {
    Users, Clock, MessageSquare, Calendar, Loader2, Star, Building,
    TrendingUp, Bell, CheckCircle, ArrowUp, ArrowDown, Activity, AlertCircle, FileText, Search
} from 'lucide-react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area
} from 'recharts';
import api from '../services/api';
import { socket } from '../services/socket';

// ==============================================================================
// 1. HELPERS E UTILITÁRIOS
// ==============================================================================

// Formata segundos para "2h 30m" ou "45m 10s"
const formatDuration = (seconds) => {
    if (!seconds || isNaN(seconds)) return '00:00';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m ${s}s`;
};

// ==============================================================================
// 2. COMPONENTES VISUAIS (WIDGETS)
// ==============================================================================

const KPICard = ({ title, value, sub, icon: Icon, color, onClick }) => (
    <div
        onClick={onClick}
        className={`bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex justify-between items-center transition-all ${onClick ? 'cursor-pointer hover:shadow-md hover:border-blue-200' : ''}`}
    >
        <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">{title}</p>
            <h3 className="text-3xl font-black mt-2 text-gray-800">{value}</h3>
            <p className="text-xs text-gray-400 mt-1 font-medium">{sub}</p>
        </div>
        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center bg-${color}-50 text-${color}-600`}>
            <Icon className="w-6 h-6" />
        </div>
    </div>
);

// ==============================================================================
// 3. COMPONENTE PRINCIPAL: DASHBOARD
// ==============================================================================

const Dashboard = ({ currentUser, navigateTo, whatsappStatus }) => {
    const [loading, setLoading] = useState(true);
    const [currentTime, setCurrentTime] = useState(new Date());

    const getLocalYYYYMMDD = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Filtro de Data (padrão: data corrente local)
    const [dateFilter, setDateFilter] = useState({
        startDate: getLocalYYYYMMDD(), // Hoje
        endDate: getLocalYYYYMMDD()     // Hoje
    });

    // Estado unificado com valores padrão para evitar erros de renderização
    const [data, setData] = useState({
        metrics: {
            totalChats: 0,
            waitingChats: 0,
            activeChats: 0,
            endedToday: 0,
            avgWaitTime: 0,
            messagesSent: 0,
            messagesReceived: 0,
            peakHours: [],
            chartData: [],
            topClients: [],
            agentPerformance: [], // [{ name: 'Nome', count: 10, online: true }]
            recentActivity: []    // [{ clientName: 'Cliente', status: 'open', date: ... }]
        },
        notices: [],
        todayEvents: [],
        daysRemaining: null
    });

    // Atualiza relógio a cada 60s
    useEffect(() => {
        const interval = setInterval(() => setCurrentTime(new Date()), 60000);
        return () => clearInterval(interval);
    }, []);

    // Carregamento de Dados e Listeners
    useEffect(() => {
        fetchDashboardData();

        if (socket) {
            socket.on('chat_updated', fetchDashboardData);
            socket.on('new_ticket_created', fetchDashboardData);
        }

        return () => {
            if (socket) {
                socket.off('chat_updated', fetchDashboardData);
                socket.off('new_ticket_created', fetchDashboardData);
            }
        };
    }, []); // Carrega apenas uma vez (sem dateFilter)

    const fetchDashboardData = async () => {
        // Só mostra loading na primeira carga para não piscar a tela em updates do socket
        if (!data.metrics.totalChats) setLoading(true);

        try {
            // Requisições paralelas para performance
            const [summaryRes, noticesRes, agendaRes, companyRes] = await Promise.all([
                api.get('/dashboard/summary', {
                    params: {
                        startDate: dateFilter.startDate,
                        endDate: dateFilter.endDate
                    }
                }).catch(() => ({ data: {} })),
                api.get('/notices').catch(() => ({ data: [] })),
                api.get('/agenda/today').catch(() => ({ data: [] })),
                api.get('/company/status').catch(() => ({ data: {} }))
            ]);

            const defaultMetrics = {
                totalChats: 0, waitingChats: 0, activeChats: 0, endedToday: 0, avgWaitTime: 0,
                messagesSent: 0, messagesReceived: 0, openWorkOrders: 0,
                peakHours: [], chartData: [], topClients: [], agentPerformance: [], recentActivity: [],
                teamPerformance: []
            };

            // Garante que campos opcionais existam
            const safeMetrics = { ...defaultMetrics, ...(summaryRes.data || {}) };

            let daysLeft = null;
            if (companyRes.data && companyRes.data.next_due_date) {
                const dueDate = new Date(companyRes.data.next_due_date);
                const diffTime = dueDate - new Date();
                daysLeft = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            }

            setData({
                metrics: safeMetrics,
                notices: Array.isArray(noticesRes.data) ? noticesRes.data : [],
                todayEvents: Array.isArray(agendaRes.data) ? agendaRes.data : [],
                daysRemaining: daysLeft
            });

        } catch (e) {
            console.error("Erro ao carregar dashboard:", e);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-full bg-gray-50">
                <div className="flex flex-col items-center">
                    <Loader2 className="w-12 h-12 animate-spin text-blue-600 mb-4" />
                    <p className="text-gray-500 font-medium">Carregando painel de controle...</p>
                </div>
            </div>
        );
    }

    const { metrics, notices, todayEvents, daysRemaining } = data;

    return (
        <div className="p-6 h-full overflow-y-auto bg-gray-50 font-sans custom-scrollbar">

            {/* --- ALERTA DE WHATSAPP DESCONECTADO --- */}
            {whatsappStatus === 'DISCONNECTED' && (
                <div 
                    onClick={() => navigateTo && navigateTo('settings')}
                    className="mb-6 p-4 bg-red-600 text-white rounded-xl shadow-lg border border-red-700 flex items-center justify-between cursor-pointer hover:bg-red-700 transition-colors animate-pulse-slow"
                >
                    <div className="flex items-center gap-3">
                        <AlertCircle className="w-8 h-8 text-red-100" />
                        <div>
                            <h4 className="font-extrabold text-lg flex items-center gap-2">
                                Sistema Inoperante: WhatsApp Desconectado!
                            </h4>
                            <p className="text-red-100 text-sm">
                                O seu sistema está com o envio/recebimento paralizado pois o seu WhatsApp perdeu a conexão. Clique aqui, acesse as Configurações e gere um novo QR Code.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* --- ALERTA DE VENCIMENTO DA ASSINATURA --- */}
            {daysRemaining !== null && daysRemaining <= 7 && (
                <div 
                    onClick={() => navigateTo && navigateTo('assinatura')}
                    className="mb-8 p-4 bg-red-600 text-white rounded-xl shadow-lg border border-red-700 flex items-center justify-between cursor-pointer hover:bg-red-700 transition-colors animate-pulse-slow"
                >
                    <div className="flex items-center gap-3">
                        <AlertCircle className="w-8 h-8 text-red-100" />
                        <div>
                            <h4 className="font-extrabold text-lg flex items-center gap-2">
                                ATENÇÃO: Aviso de Vencimento
                            </h4>
                            <p className="text-red-100 text-sm">
                                {daysRemaining < 0 
                                    ? `Sua assinatura está em atraso há ${Math.abs(daysRemaining)} dias. Por favor, regularize para evitar a suspensão da plataforma.`
                                    : daysRemaining === 0
                                        ? "Sua assinatura vence hoje! Renove agora para evitar interrupções."
                                        : `Sua assinatura vencerá em ${daysRemaining} dias. Clique aqui para regularizar sua pendência e evitar a suspensão da plataforma.`
                                }
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* --- CABEÇALHO --- */}
            <header className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-end animate-in fade-in slide-in-from-top-4 duration-500">
                <div>
                    <h1 className="text-3xl font-black text-gray-800 tracking-tight">Visão Geral</h1>
                    <p className="text-gray-500 text-sm mt-1 flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {currentTime.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                        <span className="mx-2">•</span>
                        {currentTime.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    {/* Filtro de Data */}
                    <div className="flex items-center gap-2 bg-white border border-gray-200 px-3 py-2 rounded-lg shadow-sm">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        <input
                            type="date"
                            value={dateFilter.startDate}
                            onChange={(e) => setDateFilter(prev => ({ ...prev, startDate: e.target.value }))}
                            className="border-none outline-none text-sm w-32"
                        />
                        <span className="text-gray-400 text-sm">até</span>
                        <input
                            type="date"
                            value={dateFilter.endDate}
                            onChange={(e) => setDateFilter(prev => ({ ...prev, endDate: e.target.value }))}
                            className="border-none outline-none text-sm w-32"
                        />
                    </div>

                    {/* Botão Buscar (Lupa) */}
                    <button
                        onClick={fetchDashboardData}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-bold transition-colors shadow-sm flex items-center gap-2"
                        title="Buscar dados do período"
                    >
                        <Search className="w-4 h-4" />
                        Buscar
                    </button>
                </div>
            </header>

            {/* --- BLOCO 1: KPIs PRINCIPAIS --- */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                <KPICard title="Total" value={metrics.totalChats} sub="Chamados" icon={MessageSquare} color="blue" />
                <KPICard title="Fila de Espera" value={metrics.waitingChats} sub="Aguardando" icon={Clock} color="yellow" onClick={() => navigateTo && navigateTo('chat')} />
                <KPICard title="Em Atendimento" value={metrics.activeChats} sub="Ativos" icon={Users} color="indigo" onClick={() => navigateTo && navigateTo('chat')} />
                <KPICard title="Finalizados" value={metrics.endedToday} sub="Concluídos" icon={CheckCircle} color="green" />
            </div>

            {/* --- BLOCO 2: KPIs SECUNDÁRIOS (TMA, Mensagens e OS) --- */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <KPICard title="Tempo Médio (TMA)" value={formatDuration(metrics.avgWaitTime)} sub="Duração Média" icon={Clock} color="purple" />
                <KPICard title="Msg Recebidas" value={metrics.messagesReceived} sub="Período" icon={ArrowDown} color="teal" />
                <KPICard title="Msg Enviadas" value={metrics.messagesSent} sub="Período" icon={ArrowUp} color="orange" />
                <KPICard title="OS em Aberto" value={metrics.openWorkOrders || 0} sub="Ordens de Serviço" icon={FileText} color="red" />
            </div>

            {/* --- BLOCO 3: GRÁFICOS --- */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {/* Gráfico de Volume Semanal */}
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                    <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center">
                        <TrendingUp className="w-5 h-5 mr-2 text-blue-600" /> Volume Semanal
                    </h3>
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={metrics.chartData || []}>
                                <defs>
                                    <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: '#9ca3af', fontSize: 12 }} dy={10} />
                                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#9ca3af', fontSize: 12 }} />
                                <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }} />
                                <Area type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={3} fillOpacity={1} fill="url(#colorCount)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Gráfico de Horário de Pico */}
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                    <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center">
                        <Clock className="w-5 h-5 mr-2 text-orange-500" /> Horários de Pico
                    </h3>
                    <div className="h-64">
                        {metrics.peakHours && metrics.peakHours.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={metrics.peakHours}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                    <XAxis dataKey="hour" axisLine={false} tickLine={false} tick={{ fill: '#9ca3af', fontSize: 10 }} interval={2} />
                                    <YAxis axisLine={false} tickLine={false} tick={{ fill: '#9ca3af', fontSize: 12 }} />
                                    <Tooltip cursor={{ fill: '#f3f4f6' }} contentStyle={{ borderRadius: '12px', border: 'none' }} />
                                    <Bar dataKey="avgCount" name="Quantidade" fill="#f97316" radius={[4, 4, 0, 0]} barSize={20} activeBar={{ fill: '#ea580c' }} />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                                <span className="flex flex-col items-center gap-2">
                                    <Clock className="w-8 h-8 opacity-20" />
                                    Aguardando dados de horário...
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Gráfico dias da semana */}
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 col-span-1 lg:col-span-2">
                    <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center">
                        <Calendar className="w-5 h-5 mr-2 text-purple-600" /> Atendimentos por Dia da Semana
                    </h3>
                    <div className="h-64">
                        {metrics.busyDays && metrics.busyDays.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={metrics.busyDays}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                    <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fill: '#9ca3af', fontSize: 12 }} />
                                    <YAxis axisLine={false} tickLine={false} tick={{ fill: '#9ca3af', fontSize: 12 }} />
                                    <Tooltip cursor={{ fill: '#f3f4f6' }} contentStyle={{ borderRadius: '12px', border: 'none' }} />
                                    <Bar dataKey="count" name="Total" fill="#8b5cf6" radius={[4, 4, 0, 0]} barSize={40} activeBar={{ fill: '#7c3aed' }} />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                                <span className="flex flex-col items-center gap-2">
                                    <Calendar className="w-8 h-8 opacity-20" />
                                    Aguardando dados semanais...
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* --- BLOCO 4: OPERACIONAL (Tabelas e Listas) --- */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">

                {/* COLUNA ESQUERDA (2/3): Equipe e Clientes */}
                < div className="xl:col-span-2 space-y-6" >

                    {/* Performance da Equipe */}
                    < div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100" >
                        <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                            <Users className="w-5 h-5 mr-2 text-indigo-600" /> Performance da Equipe
                        </h3>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left text-gray-500">
                                <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 rounded-l-lg">Atendente</th>
                                        <th className="px-4 py-3 text-center">Total</th>
                                        <th className="px-4 py-3 text-center">Finalizados</th>
                                        <th className="px-4 py-3 text-center">Cancelados</th>
                                        <th className="px-4 py-3 text-center rounded-r-lg">Taxa de Solução</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(metrics.teamPerformance || []).map((agent, idx) => (
                                        <tr key={idx} className="bg-white border-b hover:bg-gray-50 last:border-0">
                                            <td className="px-4 py-3 font-medium text-gray-900 flex items-center">
                                                <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center mr-3 text-xs font-bold uppercase">
                                                    {agent.name.charAt(0)}
                                                </div>
                                                {agent.name}
                                            </td>
                                            <td className="px-4 py-3 text-center font-bold text-gray-900">{agent.total}</td>
                                            <td className="px-4 py-3 text-center font-bold text-green-600">{agent.finished}</td>
                                            <td className="px-4 py-3 text-center font-bold text-red-600">{agent.canceled}</td>
                                            <td className="px-4 py-3 text-center">
                                                <span className={`px-3 py-1 rounded-full text-xs font-bold ${agent.solutionRate >= 80 ? 'bg-green-100 text-green-700' :
                                                    agent.solutionRate >= 60 ? 'bg-yellow-100 text-yellow-700' :
                                                        'bg-red-100 text-red-700'
                                                    }`}>
                                                    {agent.solutionRate}%
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                    {(!metrics.teamPerformance || metrics.teamPerformance.length === 0) && (
                                        <tr><td colSpan="5" className="px-4 py-8 text-center text-gray-400">Nenhum dado de performance disponível.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div >

                    {/* Top Clientes */}
                    < div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col" >
                        <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center">
                            <Star className="w-5 h-5 mr-2 text-yellow-500" /> Top Clientes
                        </h3>
                        <div className="flex-1 overflow-y-auto custom-scrollbar pr-2 space-y-3 max-h-60">
                            {(metrics.topClients || []).map((client, idx) => (
                                <div key={idx} className="flex items-center p-3 hover:bg-gray-50 rounded-xl transition-colors border border-transparent hover:border-gray-100">
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white shadow-sm shrink-0 ${['bg-blue-500', 'bg-purple-500', 'bg-pink-500', 'bg-orange-500', 'bg-teal-500'][idx % 5]}`}>
                                        {client.name ? client.name.charAt(0).toUpperCase() : '#'}
                                    </div>
                                    <div className="ml-4 flex-1 min-w-0">
                                        <p className="text-sm font-bold text-gray-800 truncate">{client.name}</p>
                                        <p className="text-xs text-gray-500 truncate flex items-center">
                                            <Building className="w-3 h-3 mr-1" /> {client.company || 'Particular'}
                                        </p>
                                    </div>
                                    <span className="bg-gray-100 text-gray-600 text-xs font-bold px-2 py-1 rounded-lg border border-gray-200">
                                        {client.count} chats
                                    </span>
                                </div>
                            ))}
                            {(!metrics.topClients || metrics.topClients.length === 0) && <p className="text-gray-400 text-center py-4">Sem dados de clientes.</p>}
                        </div>
                    </div >
                </div >

                {/* COLUNA DIREITA (1/3): Widgets Laterais */}
                < div className="space-y-6" >

                    {/* Mural de Avisos */}
                    < div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100" >
                        <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                            <Bell className="w-5 h-5 mr-2 text-red-500" /> Mural de Avisos
                        </h3>
                        <div className="space-y-3 max-h-48 overflow-y-auto custom-scrollbar">
                            {(notices || []).length > 0 ? notices.map((notice) => (
                                <div key={notice._id || notice.id} className="bg-yellow-50 p-3 rounded-lg border border-yellow-100">
                                    <p className="text-sm text-gray-800 font-medium">{notice.message}</p>
                                    <div className="flex justify-between items-center mt-2">
                                        <span className="text-[10px] font-bold text-yellow-700 bg-yellow-100 px-2 py-0.5 rounded-full">{notice.authorName}</span>
                                        <span className="text-[10px] text-gray-400">{new Date(notice.createdAt).toLocaleDateString()}</span>
                                    </div>
                                </div>
                            )) : <div className="text-center py-4 text-gray-400 text-sm">Nenhum aviso no mural.</div>}
                        </div>
                    </div >

                    {/* Agenda do Dia */}
                    < div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100" >
                        <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                            <Calendar className="w-5 h-5 mr-2 text-blue-600" /> Agenda do Dia
                        </h3>
                        <div className="space-y-3 max-h-48 overflow-y-auto custom-scrollbar">
                            {(todayEvents || []).length > 0 ? todayEvents.map((event) => (
                                <div key={event._id || event.id} className="flex items-start p-2 border-b border-gray-50 last:border-0 hover:bg-gray-50 rounded">
                                    <div className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs font-bold mr-3 min-w-[50px] text-center">
                                        {event.time || 'Dia'}
                                    </div>
                                    <div>
                                        <p className="text-sm font-bold text-gray-800">{event.title}</p>
                                        {event.description && <p className="text-xs text-gray-500 line-clamp-1">{event.description}</p>}
                                    </div>
                                </div>
                            )) : (
                                <div className="text-center py-4 text-gray-400 text-sm flex flex-col items-center">
                                    <AlertCircle className="w-6 h-6 mb-1 opacity-50" />
                                    Sem eventos agendados hoje.
                                </div>
                            )}
                        </div>
                    </div >

                    {/* Atividade Recente */}
                    < div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100" >
                        <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                            <Activity className="w-5 h-5 mr-2 text-green-600" /> Recentes
                        </h3>
                        <div className="space-y-4 max-h-60 overflow-y-auto custom-scrollbar pr-2">
                            {(metrics.recentActivity || []).map((activity, idx) => (
                                <div key={idx} className="flex items-start border-b border-gray-50 pb-3 last:border-0 last:pb-0">
                                    <div className="flex-shrink-0">
                                        <span className={`flex items-center justify-center w-8 h-8 rounded-full ring-4 ring-white ${activity.status === 'open' ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600'}`}>
                                            <MessageSquare className="w-4 h-4" />
                                        </span>
                                    </div>
                                    <div className="ml-4 flex-1">
                                        <p className="text-sm font-medium text-gray-900">{activity.clientName || 'Cliente'}</p>
                                        <p className="text-xs text-gray-500">
                                            Status: <span className={`font-bold ${activity.status === 'open' ? 'text-blue-600' : 'text-green-600'}`}>
                                                {activity.status === 'open' ? 'Em Atendimento' : 'Finalizado'}
                                            </span>
                                        </p>
                                    </div>
                                    <div className="text-right text-xs text-gray-400">
                                        {new Date(activity.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </div>
                                </div>
                            ))}
                            {(!metrics.recentActivity || metrics.recentActivity.length === 0) && (
                                <p className="text-center text-gray-400 py-4 text-sm">Nenhuma atividade recente.</p>
                            )}
                        </div>
                    </div >

                </div >
            </div >
        </div >
    );
};

export default Dashboard;